import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import Navigation from '../components/Navigation';
import Logo from '../components/Logo';
import { IEContent } from './IE';
import { PMApprovalTab } from '../components/SampleBulletin';
import { PPCContent } from './PPC';
import { BulletinContent } from './ProductionBulletin';
import { CentreBoardContent } from './CentreBoard';
import { ListFooter } from '../ui';

export function ProductionWOContent() {
  const [tab, setTab] = useState('workorders');
  const [workOrders, setWorkOrders] = useState([]);
  const [qcSummary, setQcSummary] = useState([]);
  const [summary, setSummary] = useState({});
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddWO, setShowAddWO] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [woRes, sumRes, qcRes, ordRes] = await Promise.all([
        api.get('/production/work-orders'),
        api.get('/production/summary'),
        api.get('/production/qc-summary'),
        api.get('/orders')
      ]);
      setWorkOrders(woRes.data.data || []);
      setSummary(sumRes.data.data || {});
      setQcSummary(qcRes.data.data || []);
      setOrders(ordRes.data.data || []);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddWO = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post('/production/work-orders', form);
      setShowAddWO(false);
      setForm({});
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create work order');
    } finally {
      setSaving(false);
    }
  };

  // Output logging retired — daily output now lives on PPC → Planning (line × day actuals
  // with rejects/rework). QC lives in the Quality module. handleLogOutput/handleLogQC removed.

  const statusBadge = (status) => {
    if (!status) return null;
    const map = {
      pending:   { bg: '#F1EFE8', color: '#5F5E5A' },
      cutting:   { bg: '#EEEDF8', color: '#2A2970' },
      stitching: { bg: '#EEEDFE', color: '#3C3489' },
      finishing: { bg: '#FAEEDA', color: '#854F0B' },
      qc:        { bg: '#FAC775', color: '#412402' },
      packing:   { bg: '#EEEDF8', color: '#2A2970' },
      complete:  { bg: '#EEEDF8', color: '#2A2970' },
    };
    const s = map[status] || { bg: '#F1EFE8', color: '#5F5E5A' };
    return (
      <span style={{ ...s, padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 500 }}>
        {status}
      </span>
    );
  };

  const inputStyle = {
    width: '100%', padding: '8px 10px', border: '1px solid #ddd',
    borderRadius: 6, fontSize: 13, boxSizing: 'border-box', marginTop: 4
  };
  const labelStyle = { fontSize: 12, fontWeight: 500, color: '#555', display: 'block', marginBottom: 8 };


  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading Production...</div>;

  return (
    <>
      <div style={{ padding: 24 }}>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
          {[
            { label: 'Active Work Orders', value: summary.active_work_orders || 0, color: '#3B3A8F' },
            { label: 'Completed', value: summary.completed_work_orders || 0, color: '#E22D2D' },
            { label: "Today's Output", value: summary.todays_output == null ? 'No data yet' : parseInt(summary.todays_output).toLocaleString(), color: summary.todays_output == null ? '#aaa' : '#EF9F27', sub: summary.todays_output == null ? 'no line actuals entered today' : 'stitching line' },
            { label: 'Rejection Rate', value: summary.rejection_rate == null ? 'No data yet' : summary.rejection_rate + '%', color: summary.rejection_rate == null ? '#aaa' : (summary.rejection_rate > 3 ? '#E24B4A' : '#166534'), sub: summary.rejection_rate == null ? 'no production entered today' : null },
          ].map((kpi, i) => (
            <div key={i} style={{ background: 'white', borderRadius: 10, padding: '16px 20px', boxShadow: 'none', border: '1px solid #E8E7E7' }}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>{kpi.label}</div>
              <div style={{ fontSize: kpi.value === 'No data yet' ? 16 : 28, fontWeight: 700, color: kpi.color }}>{kpi.value}</div>
              {kpi.sub && <div style={{ fontSize: 10, color: '#aaa', marginTop: 4 }}>{kpi.sub}</div>}
            </div>
          ))}
        </div>

        <div style={{ background: 'white', borderRadius: 10, boxShadow: 'none', border: '1px solid #E8E7E7', overflow: 'hidden' }}>
          <div style={{ display: 'flex', borderBottom: '1px solid #eee', padding: '0 20px' }}>
            {[
              { key: 'workorders', label: 'Work Orders' },
              { key: 'qc', label: 'QC Summary' },
            ].map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} style={{
                padding: '14px 20px', border: 'none', background: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: tab === t.key ? 600 : 400,
                color: tab === t.key ? '#3B3A8F' : '#888',
                borderBottom: tab === t.key ? '2px solid #1D5EA8' : '2px solid transparent',
                marginBottom: -1
              }}>
                {t.label}
              </button>
            ))}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
              {tab === 'workorders' && (
                <button onClick={() => { setForm({}); setError(''); setShowAddWO(true); }}
                  style={{ padding: '7px 16px', background: '#E22D2D', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', margin: '10px 0' }}>
                  + New Work Order
                </button>
              )}
            </div>
          </div>

          <div style={{ padding: 20 }}>
            {tab === 'workorders' && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#F7F6F3' }}>
                    {['WO No.', 'Order', 'Buyer', 'Style', 'Planned', 'Output', 'Rejected', 'Status'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: '#888', fontWeight: 500, borderBottom: '1px solid #eee' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {workOrders.length === 0 ? (
                    <tr><td colSpan={8} style={{ padding: 20, color: '#aaa', textAlign: 'center' }}>No work orders yet. Click + New Work Order to create one.</td></tr>
                  ) : workOrders.map(wo => (
                    <tr key={wo.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '10px 12px', color: '#3B3A8F', fontWeight: 500 }}>{wo.wo_no}</td>
                      <td style={{ padding: '10px 12px', color: '#666' }}>{wo.order_no}</td>
                      <td style={{ padding: '10px 12px' }}>{wo.buyer_name}</td>
                      <td style={{ padding: '10px 12px', color: '#666' }}>{wo.style_no}</td>
                      <td style={{ padding: '10px 12px' }}>{parseInt(wo.planned_qty).toLocaleString()}</td>
                      <td style={{ padding: '10px 12px', color: '#E22D2D', fontWeight: 500 }}>{parseInt(wo.total_output).toLocaleString()}</td>
                      <td style={{ padding: '10px 12px', color: wo.total_rejected > 0 ? '#E24B4A' : '#aaa' }}>{wo.total_rejected}</td>
                      <td style={{ padding: '10px 12px' }}>{statusBadge(wo.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {tab === 'workorders' && <ListFooter shown={workOrders.length} total={workOrders.length} noun="work orders" />}

            {tab === 'qc' && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#F7F6F3' }}>
                    {['WO No.', 'Order', 'Buyer', 'Checked', 'Passed', 'Failed', 'Pass Rate', 'Rejection Rate'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: '#888', fontWeight: 500, borderBottom: '1px solid #eee' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {qcSummary.length === 0 ? (
                    <tr><td colSpan={8} style={{ padding: 20, color: '#aaa', textAlign: 'center' }}>No QC records yet.</td></tr>
                  ) : qcSummary.map((q, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '10px 12px', color: '#3B3A8F', fontWeight: 500 }}>{q.wo_no}</td>
                      <td style={{ padding: '10px 12px', color: '#666' }}>{q.order_no}</td>
                      <td style={{ padding: '10px 12px' }}>{q.buyer_name}</td>
                      <td style={{ padding: '10px 12px' }}>{parseInt(q.total_checked).toLocaleString()}</td>
                      <td style={{ padding: '10px 12px', color: '#E22D2D', fontWeight: 500 }}>{parseInt(q.total_passed).toLocaleString()}</td>
                      <td style={{ padding: '10px 12px', color: '#E24B4A' }}>{parseInt(q.total_failed).toLocaleString()}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ background: parseFloat(q.pass_rate_pct) >= 97 ? '#EEEDF8' : '#FAEEDA', color: parseFloat(q.pass_rate_pct) >= 97 ? '#2A2970' : '#854F0B', padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 500 }}>
                          {q.pass_rate_pct}%
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ background: parseFloat(q.rejection_rate_pct) > 3 ? '#FCEBEB' : '#EEEDF8', color: parseFloat(q.rejection_rate_pct) > 3 ? '#A32D2D' : '#2A2970', padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 500 }}>
                          {q.rejection_rate_pct}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {tab === 'qc' && <ListFooter shown={qcSummary.length} total={qcSummary.length} noun="QC records" />}
          </div>
        </div>
      </div>

      {showAddWO && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 28, width: 500 }}>
            <div style={{ background: '#2D2C72', margin: '-28px -28px 18px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}><h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>New Work Order</h3></div>
            {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>{error}</div>}
            <form onSubmit={handleAddWO}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>WO Number *</label>
                  <input style={inputStyle} placeholder="WO-2001" required onChange={e => setForm({ ...form, wo_no: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Order *</label>
                  <select style={inputStyle} required onChange={e => setForm({ ...form, order_id: e.target.value })}>
                    <option value="">Select order</option>
                    {orders.map(o => <option key={o.id} value={o.id}>{o.order_no} — {o.buyer_name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Planned Qty *</label>
                  <input style={inputStyle} type="number" placeholder="5000" required onChange={e => setForm({ ...form, planned_qty: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Start Date *</label>
                  <input style={inputStyle} type="date" required onChange={e => setForm({ ...form, start_date: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>End Date *</label>
                  <input style={inputStyle} type="date" required onChange={e => setForm({ ...form, end_date: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Notes</label>
                  <input style={inputStyle} placeholder="Optional" onChange={e => setForm({ ...form, notes: e.target.value })} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowAddWO(false)} style={{ padding: '8px 18px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                <button type="submit" disabled={saving} style={{ padding: '8px 18px', background: '#E22D2D', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                  {saving ? 'Saving...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Log Output / Log QC modals retired — output → PPC Planning actuals, QC → Quality module. */}
    </>
  );
}

const PROD_TABS = [
  { key: 'ie', label: '🏭 IE' },
  { key: 'sample_approval', label: '✅ Sample Approval', roles: ['management', 'production_incharge', 'admin'] },
  { key: 'ppc', label: '🗓 PPC' },
  { key: 'bulletin', label: '📋 Bulletin' },
  { key: 'production', label: '⚙️ Production' },
  { key: 'centreboard', label: '📊 Centre Board' },
];

export default function Production() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState('production');
  const handleLogout = async () => { await logout(); navigate('/login'); };
  const visibleTabs = PROD_TABS.filter(t => !t.roles || t.roles.includes(user?.role));
  return (
    <div style={{ minHeight: '100vh', background: 'transparent', fontFamily: 'inherit' }}>


      <div style={{ background: 'white', borderBottom: '1px solid #eee', padding: '0 24px', display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {visibleTabs.map(t => (
          <button key={t.key} onClick={() => setSection(t.key)} style={{
            padding: '12px 18px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14,
            fontWeight: section === t.key ? 700 : 400, color: section === t.key ? '#3B3A8F' : '#777',
            borderBottom: section === t.key ? '3px solid #3B3A8F' : '3px solid transparent' }}>{t.label}</button>
        ))}
      </div>

      {section === 'ie' && <IEContent />}
      {section === 'sample_approval' && <PMApprovalTab user={user} />}
      {section === 'ppc' && <PPCContent />}
      {section === 'bulletin' && <BulletinContent embedded />}
      {section === 'production' && <ProductionWOContent />}
      {section === 'centreboard' && <CentreBoardContent />}
    </div>
  );
}